import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, Sparkles, Zap, Clock, Camera, Bookmark, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import apiClient from '@/api';
import Navbar from '@/components/Navbar';

export default function MealPlanning() {
  const [groceries, setGroceries] = useState([]);
  const [newGrocery, setNewGrocery] = useState({ name: '', quantity: '', unit: 'pcs' });
  const [preferences, setPreferences] = useState('');
  const [mealPlans, setMealPlans] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [aiUsage, setAiUsage] = useState({ used_today: 0, daily_limit: 10, remaining: 10, resets_at: '' });
  const [isScanning, setIsScanning] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchMealPlans();
    fetchAiUsage();
  }, []);

  const fetchAiUsage = async () => {
    try {
      const response = await apiClient.get('/ai/usage');
      setAiUsage(response.data);
    } catch (error) {
      console.error('Failed to fetch AI usage:', error);
    }
  };

  const fetchMealPlans = async () => {
    try {
      const response = await apiClient.get('/meals');
      setMealPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch meal plans:', error);
    }
  };

  const toggleSaveMealPlan = async (plan) => {
    const nextSaved = !plan.is_saved;
    setMealPlans((prev) => prev.map((p) => (p.id === plan.id ? { ...p, is_saved: nextSaved } : p)));

    try {
      await apiClient.patch(`/meals/${plan.id}`, { is_saved: nextSaved });
    } catch (error) {
      setMealPlans((prev) => prev.map((p) => (p.id === plan.id ? { ...p, is_saved: !nextSaved } : p)));
      toast.error('Failed to update saved meal plan');
    }
  };

  const addGrocery = () => {
    if (newGrocery.name && newGrocery.quantity) {
      setGroceries([...groceries, { ...newGrocery, id: Date.now().toString() }]);
      setNewGrocery({ name: '', quantity: '', unit: 'pcs' });
    }
  };

  const removeGrocery = (id) => {
    setGroceries(groceries.filter(g => g.id !== id));
  };

  const handleScanImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsScanning(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Don't set Content-Type manually: axios needs to generate the
      // multipart boundary itself for a FormData body (a static
      // "multipart/form-data" header, even spelled correctly, has no
      // boundary= parameter and the backend can't parse it).
      const response = await apiClient.post('/meals/scan-groceries', formData);

      //merge AI results to List
      const scannedItems = response.data.items.map(item => ({
        ...item,
        id: Math.random().toString(36).substr(2, 9)// provide temp id for react keys
      }));

      setGroceries((prev) => [...prev, ...scannedItems]);
      toast.success(`${scannedItems.length} items identified from photo!`);
    } catch (error) {
      toast.error("Failed to scan image. Please try again.");
    } finally {
      setIsScanning(false);
      e.target.value = ''; // allow re-selecting the same file consecutively
    }
  };

  //allow manual editing of items
  const updateGrocery = (index, field, value) => {
    const updated = [...groceries];
    updated[index][field] = value;
    setGroceries(updated);
  };

  const updateItem = (index, field, value) => {
  setGroceries((prevGroceries) => {
    // 1. Create a new array based on the old one
    return prevGroceries.map((item, i) => {
      // 2. If it's the item we want to change...
      if (i === index) {
        // 3. Return a NEW object with the updated field
        return { ...item, [field]: value };
      }
      // 4. Otherwise, return the item as-is
      return item;
        });
      });
    };

  const generateMealPlan = async () => {
    if (groceries.length === 0) {
      toast.error('Please add at least one grocery item');
      return;
    }

    if (aiUsage.remaining <= 0) {
      toast.error('Daily AI limit reached. Try again tomorrow!');
      return;
    }

    setGenerating(true);
    try {
      const response = await apiClient.post('/meals/generate', {
        groceries,
        preferences
      });

      // Update AI usage from response
      if (response.data.ai_usage) {
        setAiUsage(response.data.ai_usage);
      }

      toast.success('Meal plan generated!');
      fetchMealPlans();
      setGroceries([]);
      setPreferences('');
    } catch (error) {
      // Handle rate limit error (429)
      if (error.response?.status === 429) {
        const detail = error.response?.data?.detail;
        setAiUsage({
          used_today: detail?.used_today || aiUsage.daily_limit,
          daily_limit: detail?.daily_limit || aiUsage.daily_limit,
          remaining: 0,
          resets_at: detail?.resets_at || ''
        });
        toast.error('Daily AI limit reached. Your limit resets at midnight UTC.');
      } else {
        toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || 'Failed to generate meal plan');
      }
    } finally {
      setGenerating(false);
    }
  };

  const renderMealPlanCard = (plan) => (
    <div
      key={plan.id}
      className="warm-card border border-[#D4C4B0] p-6 hover:border-[#D4A574]/50 transition-all duration-300"
      data-testid={`meal-plan-${plan.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="text-xl font-bold text-[#D4A574] uppercase">{plan.meal_name}</h3>
        <button
          type="button"
          onClick={() => toggleSaveMealPlan(plan)}
          data-testid={`save-meal-plan-${plan.id}`}
          aria-label={plan.is_saved ? 'Unsave meal plan' : 'Save meal plan'}
          className={`flex-shrink-0 ${plan.is_saved ? 'text-[#D4A574]' : 'text-[#A89284] hover:text-[#D4A574]'}`}
        >
          <Bookmark className="w-5 h-5" fill={plan.is_saved ? 'currentColor' : 'none'} />
        </button>
      </div>

      {plan.calories && plan.protein && (
        <div className="flex gap-4 mb-4">
          <span className="text-[#8B7355] text-sm uppercase">
            <span className="text-[#5C4A42] font-bold">{plan.calories}</span> CAL
          </span>
          <span className="text-[#8B7355] text-sm uppercase">
            <span className="text-[#5C4A42] font-bold">{plan.protein}g</span> PROTEIN
          </span>
        </div>
      )}

      <div className="mb-4">
        <p className="text-[#8B7355] text-xs uppercase font-bold mb-2">INGREDIENTS:</p>
        <ul className="list-disc list-inside text-[#5C4A42] space-y-1">
          {plan.ingredients.map((ingredient, idx) => (
            <li key={idx} className="text-sm">{ingredient}</li>
          ))}
        </ul>
      </div>

      <div>
        <p className="text-[#8B7355] text-xs uppercase font-bold mb-2">INSTRUCTIONS:</p>
        <ol className="list-decimal list-inside text-[#5C4A42] space-y-2">
          {plan.instructions.map((step, idx) => (
            <li key={idx} className="text-sm">{step}</li>
          ))}
        </ol>
      </div>
    </div>
  );

  const savedPlans = mealPlans.filter((p) => p.is_saved);
  const activePlans = mealPlans.filter((p) => !p.is_saved);

  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="meal-planning-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tight">
            AI <span className="text-volt">MEAL PLANNING</span>
          </h1>

          {/* AI Usage Indicator */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 md:mt-0 flex items-center gap-3 bg-[#2C2620] border border-[#D4C4B0] px-4 py-2 rounded-lg"
            data-testid="ai-usage-indicator"
          >
            <Zap className={`w-5 h-5 ${aiUsage.remaining > 0 ? 'text-[#D4A574]' : 'text-[#FF3300]'}`} />
            <div className="flex flex-col">
              <span className="text-xs text-[#8B7355] uppercase font-bold">Daily AI Generations</span>
              <span className="text-white font-bold">
                {aiUsage.remaining} / {aiUsage.daily_limit} remaining
              </span>
            </div>
            {aiUsage.remaining === 0 && (
              <div className="flex items-center gap-1 text-[#FF3300]">
                <Clock className="w-4 h-4" />
                <span className="text-xs">Resets at midnight UTC</span>
              </div>
            )}
          </motion.div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Grocery Input Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="warm-card border border-[#D4C4B0] p-6"
          >
            <h2 className="text-2xl font-bold uppercase mb-6">AVAILABLE GROCERIES</h2>

            <div className="space-y-4 mb-6">
              <div>
                <Label className="text-[#5C4A42] uppercase text-xs font-bold tracking-widest mb-2 block">
                  Item Name
                </Label>
                <Input
                  data-testid="grocery-name-input"
                  value={newGrocery.name}
                  onChange={(e) => setNewGrocery({ ...newGrocery, name: e.target.value })}
                  placeholder="e.g., Chicken Breast"
                  className="bg-[#FAF9F7] border-[#D4C4B0] text-[#5C4A42] focus:border-[#D4A574]"
                  onKeyPress={(e) => e.key === 'Enter' && addGrocery()}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#5C4A42] uppercase text-xs font-bold tracking-widest mb-2 block">
                    Quantity
                  </Label>
                  <Input
                    data-testid="grocery-quantity-input"
                    value={newGrocery.quantity}
                    onChange={(e) => setNewGrocery({ ...newGrocery, quantity: e.target.value })}
                    placeholder="2"
                    className="bg-[#FAF9F7] border-[#D4C4B0] text-[#5C4A42] focus:border-[#D4A574]"
                  />
                </div>
                <div>
                  <Label className="text-[#5C4A42] uppercase text-xs font-bold tracking-widest mb-2 block">
                    Unit
                  </Label>
                  <Input
                    data-testid="grocery-unit-input"
                    value={newGrocery.unit}
                    onChange={(e) => setNewGrocery({ ...newGrocery, unit: e.target.value })}
                    placeholder="kg"
                    className="bg-[#FAF9F7] border-[#D4C4B0] text-[#5C4A42] focus:border-[#D4A574]"
                  />
                </div>
              </div>

              <Button
                onClick={addGrocery}
                data-testid="add-grocery-btn"
                className="w-full bg-[#D4A574] text-white font-bold uppercase hover:bg-[#C19563]"
              >
                <Plus className="w-4 h-4 mr-2" />
                ADD ITEM
              </Button>

              <input
                type="file"
                accept="image/*"
                data-testid="grocery-photo-input"
                ref={fileInputRef}
                onChange={handleScanImage}
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={isScanning}
                data-testid="scan-photo-btn"
                variant="outline"
                className="w-full border-[#D4A574] text-[#D4A574] hover:bg-[#D4A574] hover:text-white uppercase"
              >
                <Camera className="w-4 h-4 mr-2" />
                {isScanning ? 'SCANNING...' : 'SCAN GROCERIES FROM PHOTO'}
              </Button>
            </div>

            {/* Grocery List */}
            <div className="space-y-2 mb-6" data-testid="grocery-list">
              {/* Added 'index' to the map arguments */}
              {groceries.map((grocery, index) => (
                <div
                  key={grocery.id}
                  className="flex items-center gap-4 justify-between bg-[#FAF9F7] p-3 border border-[#D4C4B0]"
                > {/* Fixed: Properly closed the opening div tag */}

                  <div className="flex gap-2 flex-1">
                    <input
                      value={grocery.name} // Changed 'item' to 'grocery'
                      onChange={(e) => updateItem(index, 'name', e.target.value)}
                      className="text-black p-1 rounded w-full"
                    />
                    <input
                      value={grocery.quantity} // Changed 'item' to 'grocery'
                      onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                      className="text-black p-1 rounded w-20"
                    />
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeGrocery(grocery.id)}
                    className="text-[#FF3300] hover:text-[#FF3300]/80"
                    data-testid={`remove-grocery-${grocery.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Preferences */}
            <div className="mb-6">
              <Label className="text-[#5C4A42] uppercase text-xs font-bold tracking-widest mb-2 block">
                PREFERENCES (OPTIONAL)
              </Label>
              <Textarea
                data-testid="preferences-input"
                value={preferences}
                onChange={(e) => setPreferences(e.target.value)}
                placeholder="e.g., High protein, low carb, vegetarian..."
                className="bg-[#FAF9F7] border-[#D4C4B0] text-[#5C4A42] focus:border-[#D4A574] min-h-[100px]"
              />
            </div>

            <Button
              onClick={generateMealPlan}
              disabled={generating || groceries.length === 0 || aiUsage.remaining <= 0}
              data-testid="generate-meal-plan-btn"
              className={`w-full font-bold uppercase transition-all duration-300 ${
                aiUsage.remaining <= 0
                  ? 'bg-gray-500 cursor-not-allowed'
                  : 'bg-[#8B7355] text-black hover:bg-[#00C8E6]'
              }`}
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {generating
                ? 'GENERATING...'
                : aiUsage.remaining <= 0
                  ? 'DAILY LIMIT REACHED'
                  : `GENERATE MEAL PLAN (${aiUsage.remaining} left)`}
            </Button>
          </motion.div>

          {/* Meal Plans History */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
            data-testid="meal-plans-list"
          >
            <h2 className="text-2xl font-bold uppercase mb-6">YOUR MEAL PLANS</h2>

            {savedPlans.length > 0 && (
              <div className="warm-card border border-[#D4C4B0]" data-testid="saved-meal-plans-panel">
                <button
                  type="button"
                  onClick={() => setShowSaved(!showSaved)}
                  data-testid="saved-meal-plans-toggle"
                  className="w-full flex items-center justify-between p-4 text-left"
                >
                  <span className="text-sm font-bold uppercase tracking-widest text-[#5C4A42] flex items-center gap-2">
                    <Bookmark className="w-4 h-4 text-[#D4A574]" fill="currentColor" />
                    SAVED ({savedPlans.length})
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-[#8B7355] transition-transform ${showSaved ? 'rotate-180' : ''}`}
                  />
                </button>
                {showSaved && (
                  <div className="px-4 pb-4 space-y-4" data-testid="saved-meal-plans-list">
                    {savedPlans.map((plan) => renderMealPlanCard(plan))}
                  </div>
                )}
              </div>
            )}

            {mealPlans.length === 0 ? (
              <div className="warm-card border border-[#D4C4B0] p-8 text-center">
                <p className="text-[#8B7355]">No meal plans yet. Generate your first one!</p>
              </div>
            ) : activePlans.length === 0 ? (
              <div className="warm-card border border-[#D4C4B0] p-8 text-center">
                <p className="text-[#8B7355]">All your meal plans are saved. Check the panel above!</p>
              </div>
            ) : (
              activePlans.map((plan) => renderMealPlanCard(plan))
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
